import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { teamAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import { UserPlus, Mail, MoreVertical, Shield, Pencil, Trash2, Loader2, User, Eye, EyeOff, Key } from 'lucide-react';
import { format } from 'date-fns';

const Team = () => {
  const { toast } = useToast();
  const { user: currentUser, hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'Staff'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Edit dialog state
  const [editDialog, setEditDialog] = useState(false);
  const [editMember, setEditMember] = useState(null);
  
  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteMember, setDeleteMember] = useState(null);
  
  // Change password dialog state
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordMember, setPasswordMember] = useState(null);
  const [isOwnPassword, setIsOwnPassword] = useState(false);

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      const membersRes = await teamAPI.getMembers();
      setMembers(membersRes.data.members || []);
    } catch (error) {
      console.error('Error fetching team data:', error);
      toast({ title: 'Error', description: 'Failed to load team data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.firstName.trim() || !newUser.lastName.trim() || !newUser.email.trim() || !newUser.password.trim()) {
      toast({ title: 'Error', description: 'All fields are required', variant: 'destructive' });
      return;
    }
    
    if (newUser.password.length < 6) {
      toast({ title: 'Error', description: 'Password must be at least 6 characters', variant: 'destructive' });
      return;
    }
    
    try {
      setSaving(true);
      await teamAPI.addUser(newUser);
      toast({ title: 'Success', description: `User ${newUser.firstName} ${newUser.lastName} created successfully` });
      setNewUser({ firstName: '', lastName: '', email: '', password: '', role: 'Staff' });
      setShowAddUserForm(false);
      fetchTeamData();
    } catch (error) {
      console.error('Error creating user:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create user', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (member) => {
    setEditMember({
      ...member,
      name: member.name || '',
      email: member.email || '',
      role: member.role || 'Staff',
      status: member.status || 'active'
    });
    setEditDialog(true);
  };

  const handleEditMember = async () => {
    if (!editMember || !editMember.name || !editMember.email) {
      toast({ title: 'Error', description: 'Name and email are required', variant: 'destructive' });
      return;
    }
    
    try {
      await teamAPI.updateMember(editMember._id, {
        name: editMember.name,
        email: editMember.email,
        role: editMember.role,
        status: editMember.status,
        avatar: editMember.avatar
      });
      toast({ title: 'Success', description: `${editMember.name} updated successfully` });
      setEditDialog(false);
      setEditMember(null);
      fetchTeamData();
    } catch (error) {
      console.error('Error updating member:', error);
      toast({ title: 'Error', description: 'Failed to update team member', variant: 'destructive' });
    }
  };

  const openDeleteDialog = (member) => {
    setDeleteMember(member);
    setDeleteDialog(true);
  };

  const handleDeleteMember = async () => {
    if (!deleteMember) return;
    
    try {
      await teamAPI.removeMember(deleteMember._id);
      toast({ title: 'Success', description: `${deleteMember.name} removed from team` });
      setDeleteDialog(false);
      setDeleteMember(null);
      fetchTeamData();
    } catch (error) {
      console.error('Error deleting member:', error);
      toast({ title: 'Error', description: 'Failed to remove team member', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team</h1>
          <p className="text-gray-500 mt-1">Manage your team members and permissions</p>
        </div>
        <Button onClick={() => setShowAddUserForm(true)} className="gap-2" data-testid="add-user-btn">
          <UserPlus className="h-4 w-4" />
          Add User
        </Button>
      </div>

      {/* Add User Form */}
      {showAddUserForm && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Add New User</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-xs text-gray-600">First Name *</Label>
                <Input
                  placeholder="Enter first name..."
                  value={newUser.firstName}
                  onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                  className="bg-white"
                  data-testid="add-user-firstname-input"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Last Name *</Label>
                <Input
                  placeholder="Enter last name..."
                  value={newUser.lastName}
                  onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                  className="bg-white"
                  data-testid="add-user-lastname-input"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Email *</Label>
                <Input
                  type="email"
                  placeholder="Enter email address..."
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="bg-white"
                  data-testid="add-user-email-input"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Password *</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter password..."
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="bg-white pr-10"
                    data-testid="add-user-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Staff">Staff</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Accountant">Accountant</SelectItem>
                    <SelectItem value="Admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAddUserForm(false)}>Cancel</Button>
              <Button onClick={handleAddUser} disabled={saving} data-testid="save-user-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {saving ? 'Creating...' : 'Create User'}
              </Button>
            </div>
            <div className="mt-4 p-3 bg-white rounded border text-sm text-gray-600">
              <p className="font-medium text-gray-800 mb-1">Role Permissions:</p>
              <ul className="space-y-1 text-xs">
                <li><span className="font-medium">Staff:</span> Can add and edit items. Cannot delete anything.</li>
                <li><span className="font-medium">Manager/Accountant:</span> Can add, edit, and delete tasks, invoices, documents. Cannot delete clients.</li>
                <li><span className="font-medium">Admin:</span> Full access to all features.</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Admin Account */}
      {currentUser && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              Your Account
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-4">
                {currentUser.avatar ? (
                  <img
                    src={currentUser.avatar}
                    alt={currentUser.name}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-semibold text-lg">
                    {currentUser.firstName?.charAt(0) || currentUser.name?.charAt(0) || '?'}
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">
                      {currentUser.firstName && currentUser.lastName 
                        ? `${currentUser.firstName} ${currentUser.lastName}` 
                        : currentUser.name || 'User'}
                    </h3>
                    <Badge className="bg-blue-600 text-white">{currentUser.role || 'Admin'}</Badge>
                  </div>
                  <p className="text-sm text-gray-600">{currentUser.email}</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2"
                onClick={() => {
                  setPasswordMember(currentUser);
                  setIsOwnPassword(true);
                  setPasswordDialog(true);
                }}
                data-testid="change-own-password-btn"
              >
                <Key className="h-4 w-4" />
                Change Password
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team Members */}
      <Card>
        <CardHeader>
          <CardTitle>Team Members ({members.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <User className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p className="text-sm">No team members yet. Invite your first team member!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {members.map((member) => (
                <div
                  key={member._id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  data-testid={`team-member-${member._id}`}
                >
                  <div className="flex items-center gap-4">
                    {member.avatar ? (
                      <img
                        src={member.avatar}
                        alt={member.name}
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-semibold text-lg">
                        {member.name?.charAt(0) || '?'}
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{member.name}</h3>
                        <Badge
                          className={`text-xs ${
                            member.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {member.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600">{member.role}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Mail className="h-3 w-3 text-gray-400" />
                        <span className="text-xs text-gray-500">{member.email}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {member.activeClients || 0} Clients
                      </p>
                      <p className="text-xs text-gray-500">{member.activeTasks || 0} Active Tasks</p>
                      {member.joinedDate && (
                        <p className="text-xs text-gray-400 mt-1">
                          Joined {format(new Date(member.joinedDate), 'MMM yyyy')}
                        </p>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon" data-testid={`member-menu-${member._id}`}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(member)} data-testid={`edit-member-${member._id}`}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit Member
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => {
                            setPasswordMember(member);
                            setIsOwnPassword(false);
                            setPasswordDialog(true);
                          }}
                          data-testid={`change-password-${member._id}`}
                        >
                          <Key className="h-4 w-4 mr-2" />
                          Change Password
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => openDeleteDialog(member)}
                          className="text-red-600 focus:text-red-600"
                          data-testid={`delete-member-${member._id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remove from Team
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Member Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
          </DialogHeader>
          {editMember && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={editMember.name}
                  onChange={(e) => setEditMember({ ...editMember, name: e.target.value })}
                  data-testid="edit-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={editMember.email}
                  onChange={(e) => setEditMember({ ...editMember, email: e.target.value })}
                  data-testid="edit-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={editMember.role} onValueChange={(v) => setEditMember({ ...editMember, role: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Staff">Staff</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Accountant">Accountant</SelectItem>
                    <SelectItem value="Senior Accountant">Senior Accountant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editMember.status} onValueChange={(v) => setEditMember({ ...editMember, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditMember} data-testid="save-edit-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to remove <strong>{deleteMember?.name}</strong> from the team? 
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteMember} data-testid="confirm-delete-btn">
              Remove Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={passwordDialog}
        onOpenChange={setPasswordDialog}
        user={passwordMember}
        isOwnAccount={isOwnPassword}
        onSuccess={() => {
          setPasswordMember(null);
          setIsOwnPassword(false);
        }}
      />
    </div>
  );
};

export default Team;
